// ScenarioValidator.jsx - All React Hook dependencies properly fixed
import React, { useState, useEffect, useCallback } from 'react';

const ScenarioValidator = ({ blocks, onValidationChange }) => {
  const [issues, setIssues] = useState([]);
  const [statistics, setStatistics] = useState({});

  // Move calculatePossiblePaths inside useCallback to fix dependency issues
  const validateScenario = useCallback(() => {
    const calculatePossiblePaths = (startingBlocks) => {
      let totalPaths = 0;
      
      const countPaths = (blockId, visited = new Set()) => {
        if (visited.has(blockId)) return 0; // Prevent infinite loops
        
        const block = blocks.find(b => b.id === blockId);
        if (!block) return 0;
        
        visited.add(blockId);
        
        if (block.type === 'event') {
          if (!block.next || block.next.length === 0) {
            return 1; // End of path
          }
          
          let paths = 0;
          block.next.forEach(nextId => {
            paths += countPaths(nextId, new Set(visited));
          });
          return paths;
        } else if (block.type === 'choice') {
          if (!block.choices || block.choices.length === 0) {
            return 1; // End of path (though this is likely an error)
          }
          
          let paths = 0;
          block.choices.forEach(choice => {
            if (choice.targetId) {
              paths += countPaths(choice.targetId, new Set(visited));
            }
          });
          return paths;
        }
        
        return 0;
      };
      
      startingBlocks.forEach(startBlock => {
        totalPaths += countPaths(startBlock.id);
      });
      
      return totalPaths;
    };

    const foundIssues = [];
    const stats = {
      totalBlocks: blocks.length,
      eventBlocks: 0,
      choiceBlocks: 0,
      totalDuration: 0,
      possiblePaths: 0,
      deadEnds: 0,
      orphanedBlocks: 0,
      incompleteConnections: 0
    };

    // Check for orphaned blocks
    const connectedBlocks = new Set();
    blocks.forEach(block => {
      if (block.type === 'event' && block.next) {
        block.next.forEach(nextId => connectedBlocks.add(nextId));
      } else if (block.type === 'choice' && block.choices) {
        block.choices.forEach(choice => {
          if (choice.targetId) connectedBlocks.add(choice.targetId);
        });
      }
    });

    // Find starting blocks (not connected to any other block)
    const startingBlocks = blocks.filter(block => !connectedBlocks.has(block.id));
    
    blocks.forEach(block => {
      if (block.type === 'event') {
        stats.eventBlocks++;
        if (block.duration) {
          stats.totalDuration += parseInt(block.duration);
        }

        // Check for dead ends
        if (!block.next || block.next.length === 0) {
          stats.deadEnds++;
        }

        // Check for missing connections
        if (block.next) {
          block.next.forEach(nextId => {
            const targetBlock = blocks.find(b => b.id === nextId);
            if (!targetBlock) {
              foundIssues.push({
                type: 'error',
                blockId: block.id,
                blockTitle: block.title,
                message: `Event block "${block.title}" connects to non-existent block: ${nextId}`
              });
              stats.incompleteConnections++;
            }
          });
        }
      } else if (block.type === 'choice') {
        stats.choiceBlocks++;

        // Check for choices without labels
        if (block.choices) {
          block.choices.forEach((choice, idx) => {
            if (!choice.label || choice.label.trim() === '') {
              foundIssues.push({
                type: 'warning',
                blockId: block.id,
                blockTitle: block.title,
                message: `Choice block "${block.title}" has unlabeled option #${idx + 1}`
              });
            }

            // Check for missing connections
            if (!choice.targetId) {
              foundIssues.push({
                type: 'warning',
                blockId: block.id,
                blockTitle: block.title,
                message: `Choice "${choice.label || `Option ${idx + 1}`}" has no target connection`
              });
              stats.incompleteConnections++;
            } else {
              const targetBlock = blocks.find(b => b.id === choice.targetId);
              if (!targetBlock) {
                foundIssues.push({
                  type: 'error',
                  blockId: block.id,
                  blockTitle: block.title,
                  message: `Choice "${choice.label}" connects to non-existent block: ${choice.targetId}`
                });
                stats.incompleteConnections++;
              }
            }
          });
        }
      }
    });

    // Check for orphaned blocks (not connected from any other block)
    blocks.forEach(block => {
      if (!startingBlocks.includes(block) && !connectedBlocks.has(block.id)) {
        foundIssues.push({
          type: 'warning',
          blockId: block.id,
          blockTitle: block.title,
          message: `Block "${block.title}" is orphaned (not connected from any other block)`
        });
        stats.orphanedBlocks++;
      }
    });

    // Calculate possible paths
    stats.possiblePaths = calculatePossiblePaths(startingBlocks);

    // Check for scenario completeness
    if (startingBlocks.length === 0) {
      foundIssues.push({
        type: 'error',
        blockId: null,
        blockTitle: null,
        message: 'No starting blocks found. All blocks are connected to other blocks.'
      });
    }

    if (startingBlocks.length > 1) {
      foundIssues.push({
        type: 'info',
        blockId: null,
        blockTitle: null,
        message: `Multiple starting points detected (${startingBlocks.length}). This creates parallel scenarios.`
      });
    }

    setIssues(foundIssues);
    setStatistics(stats);
    
    if (onValidationChange) {
      onValidationChange(foundIssues, stats);
    }
  }, [blocks, onValidationChange]);

  // Now useEffect can safely call validateScenario
  useEffect(() => {
    validateScenario();
  }, [validateScenario]);

  const getIssueIcon = (type) => {
    switch (type) {
      case 'error':
        return (
          <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'warning':
        return (
          <svg className="w-5 h-5 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        );
      case 'info':
        return (
          <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <div className="h-full bg-white">
      {/* Header */}
      <div className="border-b border-gray-200 p-4">
        <h2 className="text-lg font-semibold text-gray-800">Scenario Validation</h2>
      </div>

      {/* Statistics */}
      <div className="p-4 bg-gray-50 border-b border-gray-200">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white p-3 rounded shadow-sm">
            <div className="text-sm text-gray-500">Total Blocks</div>
            <div className="text-lg font-semibold">{statistics.totalBlocks}</div>
          </div>
          <div className="bg-white p-3 rounded shadow-sm">
            <div className="text-sm text-gray-500">Possible Paths</div>
            <div className="text-lg font-semibold">{statistics.possiblePaths}</div>
          </div>
          <div className="bg-white p-3 rounded shadow-sm">
            <div className="text-sm text-gray-500">Total Duration</div>
            <div className="text-lg font-semibold">{Math.round(statistics.totalDuration / 60)} min</div>
          </div>
          <div className="bg-white p-3 rounded shadow-sm">
            <div className="text-sm text-gray-500">Dead Ends</div>
            <div className="text-lg font-semibold">{statistics.deadEnds}</div>
          </div>
        </div>
      </div>

      {/* Issues List */}
      <div className="p-4 overflow-y-auto flex-1">
        <h3 className="font-medium text-gray-700 mb-3">
          Validation Issues {issues.length > 0 ? `(${issues.length})` : ''}
        </h3>
        
        {issues.length === 0 ? (
          <div className="flex items-center text-green-600 bg-green-50 p-4 rounded">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            No validation issues found. Your scenario looks good!
          </div>
        ) : (
          <div className="space-y-2">
            {issues.map((issue, idx) => (
              <div 
                key={idx} 
                className={`flex items-start gap-3 p-3 rounded border ${
                  issue.type === 'error' ? 'bg-red-50 border-red-200' :
                  issue.type === 'warning' ? 'bg-yellow-50 border-yellow-200' :
                  'bg-blue-50 border-blue-200'
                }`}
              >
                {getIssueIcon(issue.type)}
                <div className="flex-1">
                  <div className="text-sm">{issue.message}</div>
                  {issue.blockId && (
                    <div className="text-xs text-gray-500 mt-1">
                      Block: {issue.blockTitle || issue.blockId}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ScenarioValidator;